import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NotebookService } from "../services/notebook.service";
import { NotebookDisplayed } from "../interfaces/notebook-displayed";
import { CommonModule } from "@angular/common";
import { ConvertBytesToGiBPipe } from "../pipes/bytes-to-gib.pipe";
import { MatMenuModule } from "@angular/material/menu";
import {
  interval,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timer,
} from "rxjs";
import { handleNotebookResponse } from "../utility-functions/response-handler";
import { MatIconModule } from "@angular/material/icon";
import { NotebookActionsMenuComponent } from "../components/notebook-actions-menu/notebook-actions-menu.component";
import { SnackbarService } from "../services/snackbar.service";
import { ConsumptionService } from "../services/consumption.service";

import { UserUsage } from "../interfaces/user-usage";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "../components/confirm-dialog/confirm-dialog.component";
import {
  STOP_QUOTA_MESSAGE,
  START_QUOTA_MESSAGE,
  DELETE_MESSAGE,
  NOTEBOOK_TARGET_STATUSES,
  NOTEBOOK_TRANSITIONAL_STATUSES,
  NOTEBOOK_STATUS_LABELS,
  DEFAULT_MAX_USER_USAGE,
  GATEWAY_ERROR_MESSAGE,
  DELETE_FAILED_NOTEBOOKS,
  UPDATE_STATUS_DELAY,
  SOMETHING_WENT_WRONG_MESSAGE,
} from "../config/notebook-config";
//import { FailedNotebookBannerService } from "../services/failed-notebook-banner.service";
@Component({
  standalone: true,
    selector: "app-notebook-details",
    templateUrl: "./notebook-details.component.html",
    styleUrls: ["./notebook-details.component.css"],
    imports: [
        CommonModule,
        ConvertBytesToGiBPipe,
        MatMenuModule,
        MatIconModule,
        NotebookActionsMenuComponent,
        MatDialogModule,
    ]
})
export class NotebookDetailsComponent implements OnInit {
  readonly stopQuotaMessage = STOP_QUOTA_MESSAGE; //quota check
  readonly startQuotaMessage = START_QUOTA_MESSAGE; //quota check
  readonly deleteMessage = DELETE_MESSAGE; 
  readonly notebookTargetStatuses = NOTEBOOK_TARGET_STATUSES;
  readonly transitionalStatuses = NOTEBOOK_TRANSITIONAL_STATUSES;
  readonly maxAllowedUsage = DEFAULT_MAX_USER_USAGE;
  readonly notebookStatusLabels = NOTEBOOK_STATUS_LABELS;
  readonly gatewayQuotaMessage = GATEWAY_ERROR_MESSAGE;
  readonly deleteFailedNb = DELETE_FAILED_NOTEBOOKS;
  private recentStatusChanges = new Map<string, number>();
  private readonly updateStatusDelay = UPDATE_STATUS_DELAY; //
  //readonly somethingWentWrongMessage = SOMETHING_WENT_WRONG_MESSAGE;
  notebook?: NotebookDisplayed;
  pollingIntervalFast = 4000; //update interval for nb with transitional status
  pollingIntervalSlow = 20000; //update interval for nb with target/stable status
  private polling$?: Observable<NotebookDisplayed>;
  private deletedNotebookIds = new Set<string>();

  private destroy$ = new Subject<void>();
  usage$!: Observable<UserUsage>;
  quotaUsed$!: Observable<{
    cpu: { label: string; width: string };
    ram: { label: string; width: string };
    storage: { label: string; width: string };
  }>;

  constructor(
    private route: ActivatedRoute,
    private notebookService: NotebookService,
    private router: Router,
    private snackbarService: SnackbarService,
    private consumptionService: ConsumptionService,
    private dialog: MatDialog,
    //private bannerService: FailedNotebookBannerService
  ) {}

  //****************************** lifecycle ********************************
  ngOnInit(): void {
    this.loadNotebook();
    // Init usage
    this.consumptionService.initUsage(this.notebookService);
    this.consumptionService.initQuotaMonitoring(this.notebookService);
    this.usage$ = this.consumptionService.getCurrentUsage$();
    this.quotaUsed$ = this.consumptionService.getQuotaUsed$();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.consumptionService.cleanupQuotaMonitoring();
  }

    //****************************** handle notebooks ********************************
  private loadNotebook(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (!id) return;
    this.getAndTrackNotebookById(id);
  }

  private refreshNotebook(id: string): void {
    //const id = this.route.snapshot.paramMap.get('id');
    //if (!id) return;

    this.notebookService.getNotebookById(id).subscribe({
      next: (updatedNotebook) => {
        this.notebook = updatedNotebook;
      },
      error: (err) => console.error("Failed to refresh notebook", err),
    });
  }
  private updateNotebookWithConditionalCooldown(
    updatedNotebook: NotebookDisplayed
  ): void {
    if (!this.notebook) {
      this.notebook = updatedNotebook; // first-time load
      return;
    }

    const oldStatus = this.notebook.status?.toLowerCase();
    const newStatus = updatedNotebook.status?.toLowerCase();

    const wasTransitional = this.transitionalStatuses.includes(oldStatus || "");
    const isNowTarget = this.notebookTargetStatuses.includes(newStatus || "");

    const now = Date.now();
    const lastChange = this.recentStatusChanges.get(updatedNotebook.id) || 0;
    const withinCooldown = now - lastChange < this.updateStatusDelay;

    if (wasTransitional && isNowTarget) {
      if (withinCooldown) {
        return; // skip update
      }

      this.recentStatusChanges.set(updatedNotebook.id, now);

      setTimeout(() => {
        this.notebook = updatedNotebook;
        //this.checkIfNotebookFailed(updatedNotebook);
      }, this.updateStatusDelay);
    } else {
      this.notebook = updatedNotebook; // immediate update
      //this.checkIfNotebookFailed(updatedNotebook);
    }
  }

/*   private checkIfNotebookFailed(notebook: NotebookDisplayed): void {
    const isFailed =
      notebook.status?.toLowerCase() === this.notebookStatusLabels["failed"]; //

    if (isFailed) {
      this.bannerService.show(this.somethingWentWrongMessage);
    } else {
      this.bannerService.dismiss();
    }
  } */

  private pollStableNotebook(
    id: string,
    intervalMs: number = this.pollingIntervalSlow
  ): void {
    interval(intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.notebookService.getNotebookById(id))
      )
      .subscribe({
        next: (updatedNotebook) => {
          if (
            this.notebookTargetStatuses.includes(
              updatedNotebook.status?.toLowerCase()
            )
          ) {
            this.updateNotebookWithConditionalCooldown(updatedNotebook);
          }
        },
        error: (err) => console.error("Polling stable notebook failed", err),
      });
  }

  private getAndTrackNotebookById(id: string): void {
    this.notebookService.getNotebookById(id).subscribe({
      next: (notebook) => {
        this.notebook = notebook;
        const status = notebook.status?.toLowerCase();

        //this.checkIfNotebookFailed(notebook);
        if (this.transitionalStatuses.includes(status)) {
          // Notebook is in a transitional state- poll until stable
          this.waitForNotebookState(
            notebook.id,
            this.notebookTargetStatuses
          ).subscribe({
            next: (updatedNotebook) => {
              this.updateNotebookWithConditionalCooldown(updatedNotebook);
            },
            error: (err) => console.error("Polling failed", err),
          });
        } else if (this.notebookTargetStatuses.includes(status)) {
          this.pollStableNotebook(id);
        }

        // refresh immediately
        this.refreshNotebook(id);
      },
      error: (err) => console.error(`Failed to fetch notebook ${id}`, err),
    });
  }

  private waitForNotebookState(
    notebookId: string,
    targetStates: string[],
    intervalMs: number = this.pollingIntervalFast
  ): Observable<NotebookDisplayed> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.notebookService.getNotebookById(notebookId)),
      takeWhile(
        (notebook) => !targetStates.includes(notebook.status?.toLowerCase()),
        true
      )
    );
  }

  //****************************** notebooks actions ********************************
  deleteNotebook(notebookId: string) {
    const notebookName = this.notebook?.name || "";
    const message = `${this.deleteMessage} "${notebookName}"?`;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { message },
      backdropClass: "blurred-backdrop",
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.notebookService.deleteNotebook(notebookId).subscribe({
          next: () => {
            handleNotebookResponse(204, "Notebook delete");
            this.goHomepage();
          },
          error: (err) => {
            const customMessage = err.error?.message || err.message;
            handleNotebookResponse(
              err.status,
              "Notebook delete",
              customMessage
            );
          },
        });
      }
    });
  }

  onStopNotebook(event: { id: string }) {
    const { id } = event;
    if (!this.notebook) return;
    // refresh
    this.consumptionService.loadAndCalculateUsage(this.notebookService);
    this.stopNotebook(id);
    this.refreshNotebook(id);
  }

  private stopNotebook(notebookId: string): void {
    if (!this.notebook) return;
    if (this.transitionalStatuses.includes(this.notebook.status)) {
      console.warn(
        `Notebook ${notebookId} is already in a transitional state.`
      );
      return;
    }

    if (
      this.notebook.status.toLowerCase() !==
      this.notebookStatusLabels["running"].toLowerCase()
    ) {
      console.warn(`Notebook ${notebookId} is not in a stoppable state`);
      return;
    }

    this.notebookService.stopNotebook(notebookId).subscribe({
      next: () => {
        handleNotebookResponse(204, "Notebook stop");

        this.waitForNotebookState(notebookId, [
          this.notebookStatusLabels["stopped"],
        ]).subscribe({
          next: (updatedNotebook) => {
            // console.log('Notebook stopped:', updatedNotebook.status);
            this.updateNotebookWithConditionalCooldown(updatedNotebook);
          },
          error: (err) => {
            console.error("Polling failed", err);
          },
        });
      },
    error: (err) => {
      if (err?.status === 402 && err?.error?.message === "gateway: quota exceeded") {
        this.openSnackBarWithAction(this.stopQuotaMessage, "✕", "error");
        console.log ("GW: storage acceeded. Cant stop notebook");
      } else if (
        err?.status === 400 &&
        err?.error?.message?.includes("already stopping")
      ) {
        console.warn(`Notebook ${notebookId} is already stopping.`);
      } else {
        const customMessage = err?.error?.message || err?.message;
        handleNotebookResponse(err?.status, "Notebook stop", customMessage);
      }
    },
    });
  }

  onStartNotebook(event: { id: string; cpu: number }) {
    const { id, cpu } = event;
    if (!this.notebook) return;

    this.notebook.status = this.notebookStatusLabels["pending"];
    this.consumptionService.loadAndCalculateUsage(this.notebookService);
    // CPU quota check before starting
 
 /*    this.consumptionService
      .isCpuExceeded(cpu)
      .pipe(take(1))
      .subscribe((exceeded) => {
        if (exceeded) {
          this.openSnackBarWithAction(this.startQuotaMessage, "✕", "error");
        } else {
          this.startNotebook(id);
          this.refreshNotebook(id);
        }
      }); */
          this.startNotebook(id);
          this.refreshNotebook(id);
  }

  private startNotebook(id: string): void {
    this.notebookService.startNotebook(id).subscribe({
      next: () => {
        handleNotebookResponse(200, "Notebook started");
        this.waitForNotebookState(id, this.notebookTargetStatuses).subscribe({
          next: (updatedNotebook) => {
            this.updateNotebookWithConditionalCooldown(updatedNotebook);
          },
          error: (err) => console.error("Polling failed", err),
        });
      },
      error: (err) => {
        if (
          err.status === 402 &&
          err.error?.message === this.gatewayQuotaMessage
        ) {
          this.openSnackBarWithAction(this.startQuotaMessage, "✕", "error");
          console.log("GW: CPU limit exceeded. Can't start notebook.");
        } else {
          const customMessage = err.error?.message || err.message;
          handleNotebookResponse(err.status, "Notebook start", customMessage);
        }
      },
    });
  }

//****************************** ui interactions ********************************

  openSnackBar(msg: string) {
    this.snackbarService.openSnackBar(msg);
  }

  openSnackBarWithAction(
    msg: string,
    action: string,
    type: "success" | "error"
  ) {
    this.snackbarService.openSnackBarWithAction(msg, action, type);
  }

  //got to list of quantum emulators
  goHomepage() {
    this.router.navigateByUrl("/list");
  }
}
